import React, { useState, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Clock, MapPin } from 'lucide-react';
import { groupsApi } from '../../api/groups';
import { Card, EmptyState } from '../ui';
import { AddScheduleSlotSheet } from './AddScheduleSlotSheet';
import type { Group, GroupSchedule } from '../../types/api';

const DAY_NAMES: Record<number, string> = {
  1: 'Понедельник',
  2: 'Вторник',
  3: 'Среда',
  4: 'Четверг',
  5: 'Пятница',
  6: 'Суббота',
  0: 'Воскресенье',
};

const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Пн-Вс

function parseSchedule(schedule?: string): GroupSchedule[] {
  if (!schedule) return [];
  try {
    const parsed = JSON.parse(schedule);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

interface GroupScheduleTabProps {
  group: Group;
}

export const GroupScheduleTab: React.FC<GroupScheduleTabProps> = ({ group }) => {
  const queryClient = useQueryClient();
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [editingSlot, setEditingSlot] = useState<{ slot: GroupSchedule; index: number } | null>(null);

  const slots = useMemo(() => parseSchedule(group.schedule), [group.schedule]);

  // Group slots by day_of_week
  const slotsByDay = useMemo(() => {
    const map = new Map<number, { slot: GroupSchedule; index: number }[]>();
    slots.forEach((slot, index) => {
      const list = map.get(slot.day_of_week) || [];
      list.push({ slot, index });
      map.set(slot.day_of_week, list);
    });
    return map;
  }, [slots]);

  const updateMutation = useMutation({
    mutationFn: (newSlots: GroupSchedule[]) =>
      groupsApi.update(group.id, { schedule: JSON.stringify(newSlots) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group', group.id] });
    },
  });

  const handleAddSlot = (newSlot: GroupSchedule) => {
    const updated = [...slots, newSlot].sort((a, b) => {
      const dayDiff = DAY_ORDER.indexOf(a.day_of_week) - DAY_ORDER.indexOf(b.day_of_week);
      if (dayDiff !== 0) return dayDiff;
      return a.start_time.localeCompare(b.start_time);
    });
    updateMutation.mutate(updated);
    setShowAddSheet(false);
  };

  const handleEditSlot = (updatedSlot: GroupSchedule) => {
    if (editingSlot === null) return;
    const updated = [...slots];
    updated[editingSlot.index] = updatedSlot;
    updated.sort((a, b) => {
      const dayDiff = DAY_ORDER.indexOf(a.day_of_week) - DAY_ORDER.indexOf(b.day_of_week);
      if (dayDiff !== 0) return dayDiff;
      return a.start_time.localeCompare(b.start_time);
    });
    updateMutation.mutate(updated);
    setEditingSlot(null);
  };

  const handleDeleteSlot = (index: number) => {
    const updated = slots.filter((_, i) => i !== index);
    updateMutation.mutate(updated);
  };

  if (slots.length === 0) {
    return (
      <>
        <EmptyState
          icon={Clock}
          title="Нет расписания"
          description="Добавьте дни и время тренировок"
          primaryAction={{
            label: 'Добавить занятие',
            onClick: () => setShowAddSheet(true),
          }}
        />
        {showAddSheet && (
          <AddScheduleSlotSheet
            onSave={handleAddSlot}
            onClose={() => setShowAddSheet(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {DAY_ORDER.filter((day) => slotsByDay.has(day)).map((day) => (
          <Card key={day}>
            <h3
              className="text-[15px] font-semibold mb-3"
              style={{ color: 'var(--text-primary)' }}
            >
              {DAY_NAMES[day]}
            </h3>
            <div className="space-y-2">
              {slotsByDay.get(day)!.map(({ slot, index }) => (
                <div
                  key={index}
                  className="flex items-center justify-between rounded-xl px-3 py-2.5"
                  style={{ backgroundColor: 'var(--bg-secondary)' }}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Clock size={16} style={{ color: 'var(--accent)' }} className="flex-shrink-0" />
                    <div className="min-w-0">
                      <span
                        className="text-[15px] font-medium"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {slot.start_time} – {slot.end_time}
                      </span>
                      {slot.location && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <MapPin size={12} style={{ color: 'var(--text-hint)' }} />
                          <span
                            className="text-[13px] truncate"
                            style={{ color: 'var(--text-hint)' }}
                          >
                            {slot.location}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => setEditingSlot({ slot, index })}
                      className="p-2 rounded-lg"
                      style={{ color: 'var(--accent)' }}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteSlot(index)}
                      className="p-2 rounded-lg"
                      style={{ color: '#EF4444' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}

        {/* Add button */}
        <button
          onClick={() => setShowAddSheet(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-[15px] font-medium transition-colors"
          style={{ backgroundColor: 'var(--accent)', color: '#FFFFFF' }}
        >
          <Plus size={18} />
          Добавить занятие
        </button>
      </div>

      {showAddSheet && (
        <AddScheduleSlotSheet
          onSave={handleAddSlot}
          onClose={() => setShowAddSheet(false)}
        />
      )}

      {editingSlot && (
        <AddScheduleSlotSheet
          editSlot={editingSlot.slot}
          onSave={handleEditSlot}
          onClose={() => setEditingSlot(null)}
        />
      )}
    </>
  );
};
